import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Link,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { EGButton } from "../../../../../../MainComponents/Button/EGButton/EGButton";
import { EGBasicSelect } from "../../../../../../MainComponents/Select/EGBasicSelect";
import { IconHeader } from "../../../../../../Icon/IconHader/IconHeader";
import { EGIconButton } from "../../../../../../MainComponents/Button/IconButton/IconButton";
import { useReportContext } from "../../../../../ReportContext/useReportContext";
import { EGInput } from "../../../../../../MainComponents/Input/EGInput";
import { createSanctions, createCompensations } from "../../../../../../../apis";
import { useSnackbar } from "notistack";
import EGDatePicker from "../../../../../../MainComponents/DatePicker/EGDatePicker";
import { UnsavedChanges } from "../../../../../../Modal/Confirmation/UnsavedChanges/UnsavedChanges";
import { useReportId } from "../../../../../../../context/report/ReportContext";
import { CatalogueMultiSelect } from "../../../../../../MainComponents/Catalogues/CatalogueMultiSelect";
import { useActionsCatalogue } from "../../../../../../../hooks/useActionsCatalogue";
import {
  ACTION_TYPES,
  findAlreadyRegistered,
  getResolvedProposalKeys,
  splitProposals,
} from "../../../../../../../helpers/correctiveActions";
import { isMultipleSanctionsEnabled } from "../../../../../../../config/features/multipleSanctions";
import { InvestigationProposals } from "../InvestigationProposals/InvestigationProposals";
import { PersonRecords } from "./PersonRecords";

export const SanctionCompensationForm = ({
  closeForm,
  type,
  people,
  reloadInfo,
  reportRender,
  investigationList = [],
  // Registros ya guardados de este tipo, para no repetir lo que otro equipo hizo.
  records = [],
}) => {
  // Custom Hooks
  const id = useReportId();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { benefitOptions, sanctionsOptions, involvedType, titlesType } =
    useReportContext();
  const theme = useTheme();
  const [t] = useTranslation("report");

  const isOffenders = type === "offenders";

  // Las sanciones leen el mismo catalogo que las medidas disciplinarias que
  // propone el investigador en la conclusion. Las compensaciones no son una
  // medida correctiva, asi que conservan su lista propia.
  const { catalogue, isOwn, rootPath, isLoading: isCatalogueLoading } =
    useActionsCatalogue(isOffenders ? ACTION_TYPES.SANCTION : null);

  // Initial States
  const [isLoading, setIsLoading] = useState(false);
  // Person selected
  const [involvedSelected, setInvolvedSelected] = useState({});
  // Selected actions: [{ path, label, locales }]
  const [selectedActions, setSelectedActions] = useState([]);
  // Free text actions
  const [another, setAnother] = useState([]);
  // Sanction date
  const [determinationDate, setDeterminationDate] = useState(null);
  // Execution date
  const [executionDate, setExecutionDate] = useState(null);
  // Add comment
  const [addComment, setAddComment] = useState(false);
  // General comment about sanction
  const [comment, setComment] = useState("");
  // Show unsaved changes confirmation modal
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  // Propuestas de los investigadores, ya marcadas con su investigacion. Solo
  // aplican a sanciones: las compensaciones no las propone el investigador.
  const [proposals, setProposals] = useState([]);
  const [triedToSave, setTriedToSave] = useState(false);

  // Llave con la que el backend guarda el texto libre de cada tipo.
  const anotherKey = isOffenders ? "another_sanction" : "another_benefit";
  const actionsKey = isOffenders ? "sanctions" : "benefits";

  const multipleEnabled = isMultipleSanctionsEnabled();
  const personRecords = multipleEnabled
    ? (records || []).filter(
        (record) => record.name === involvedSelected?.data?.name
      )
    : [];

  // Solo se ofrecen las investigaciones donde aparece la persona elegida. Si el
  // involucrado vino del reporte y no de una investigacion no hay alias que
  // filtrar, asi que se ofrecen todas.
  const involvedInvestigations = involvedSelected?.aliases?.length
    ? investigationList.filter((item) =>
        involvedSelected.aliases.includes(item.alias)
      )
    : investigationList;

  // Clean form
  const resetForm = () => {
    setInvolvedSelected({});
    setSelectedActions([]);
    setAnother([]);
    setDeterminationDate(null);
    setExecutionDate(null);
    setAddComment(false);
    setComment("");
    setProposals([]);
    setTriedToSave(false);
  };

  // Detecta si el usuario capturó algo en el formulario
  const hasChanges =
    !!involvedSelected?.value ||
    selectedActions.length > 0 ||
    another.length > 0 ||
    !!determinationDate ||
    !!executionDate ||
    comment.trim() !== "" ||
    proposals.some((proposal) => proposal.confirmed === false);

  // Close & clean selected form
  const hdlCancel = () => {
    if (hasChanges) {
      setShowUnsavedModal(true);
      return;
    }
    resetForm();
    closeForm();
  };

  // Confirmar salida descartando los cambios
  const hdlConfirmLeave = () => {
    setShowUnsavedModal(false);
    resetForm();
    closeForm();
  };

  // Lo que se toma de la investigacion y lo que el resolutor decidio no aplicar
  const { confirmed, another: proposalAnother, discarded } =
    splitProposals(proposals);

  // Un descarte sin motivo deja el registro sin explicar por que no se aplico.
  const hasMissingReason = discarded.some((item) => !item.reason);

  // Validation data
  const cleanAnother = another.map((text) => text.trim()).filter(Boolean);
  const alreadyRegistered = findAlreadyRegistered({
    records: personRecords,
    actionsKey,
    actions: [...confirmed, ...selectedActions],
    another: [...proposalAnother.map((item) => item.text), ...cleanAnother],
  });
  const isInvalid =
    !involvedSelected?.value ||
    (confirmed.length === 0 &&
      proposalAnother.length === 0 &&
      selectedActions.length === 0 &&
      cleanAnother.length === 0) ||
    another.some((text) => text.trim() === "") ||
    hasMissingReason;

  // Select involved party
  const hdlChangePersonSelected = (e) => {
    const selectedPerson = people.find(
      (option) => option.value === e.target.value
    );
    setInvolvedSelected(selectedPerson);
    setTimeout(() => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "smooth",
      });
    }, 100);
  };

  // Send info
  const onSubmitActions = async () => {
    setTriedToSave(true);
    if (hasMissingReason) {
      enqueueSnackbar(t("Resolution.discard_reason_required"), {
        variant: "error",
      });
      return;
    }
    setIsLoading(true);
    const { data } = involvedSelected;
    const sending = enqueueSnackbar(`${t("Resolution.sending")}...`, {
      variant: "info",
      persist: true,
    });

    const PAYLOAD_KEYS = {
      offenders: "sanctions",
      victims: "benefits",
    };

    const API_CALLS = {
      offenders: createSanctions,
      victims: createCompensations,
    };

    const key = PAYLOAD_KEYS[type] || null;
    const endpoint = API_CALLS[type] || null;

    // Lo confirmado de la investigacion se guarda junto a lo que el resolutor
    // agrego por su cuenta: para el registro es una sola lista.
    const allActions = [...confirmed, ...selectedActions];
    // Lo capturado a mano no lleva marca de investigacion; lo confirmado si.
    const allAnother = [
      ...proposalAnother,
      ...cleanAnother.map((text) => ({ text })),
    ];

    const dataToSend = {
      report_identifier: id,
      ...data,
      ...(allActions.length > 0 && key ? { [key]: allActions } : {}),
      ...(allAnother.length > 0 && {
        another: allAnother.map((item) => ({
          [anotherKey]: item.text,
          ...(item.from_investigation && {
            from_investigation: item.from_investigation,
          }),
        })),
      }),
      ...(isOffenders && { discarded }),
      comment,
      determination_date: determinationDate
        ? determinationDate.format("YYYY-MM-DD")
        : null,
      execution_date: executionDate ? executionDate.format("YYYY-MM-DD") : null,
    };

    if (!endpoint) {
      console.error("No API function available for type:", type);
      closeSnackbar(sending);
      setIsLoading(false);
      return;
    }

    try {
      await endpoint(dataToSend);
      reloadInfo();
      closeForm();
      resetForm();
      reportRender();
      enqueueSnackbar(`${t("Resolution.data_successfully_updated")}`, {
        variant: "success",
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      closeSnackbar(sending);
    }
  };

  return (
    <Box>
      <IconHeader title={titlesType[type].title} />
      <EGBasicSelect
        label={`${titlesType[type].selectLabel}:`}
        options={people}
        optionLabel="selectLabel"
        value={involvedSelected?.value || ""}
        onChange={hdlChangePersonSelected}
        sx={{ mt: 1.5 }}
      />

      {/* Info involved party selected */}
      {involvedSelected?.value !== undefined && involvedSelected?.data && (
        <>
          <Paper
            sx={{
              my: 1.5,
              boxShadow: `0 0 4px 0 ${theme.palette.blueGray500.main}`,
              width: "100%",
              background: theme.palette.background.paper,
            }}
            elevation={0}
          >
            <ListItem dense>
              <ListItemAvatar>
                <Avatar sx={{ backgroundColor: "transparent" }}>
                  <EGIconButton
                    iconName={
                      involvedSelected.data.confirm
                        ? "outline_profile_tick"
                        : "outline_profile_remove"
                    }
                    color={
                      involvedSelected.data.confirm
                        ? "limePunch"
                        : "blueGray500"
                    }
                    title={
                      involvedSelected.data.confirm
                        ? t("Resolution.confirmed")
                        : t("Resolution.unconfirmed")
                    }
                    colorHover={
                      involvedSelected.data.confirm ? "greenII" : "blueGray700"
                    }
                  />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="h6" textAlign="center">
                    {involvedSelected.label}
                  </Typography>
                }
                secondary={
                  <Stack
                    spacing={1}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-around"
                    flexDirection="row"
                    flexWrap="wrap"
                    py={0.5}
                    component={"span"}
                  >
                    <Typography variant="body2" component="span">
                      {
                        involvedType[
                          involvedSelected.data.kind ||
                            involvedSelected.data.rol
                        ]
                      }
                    </Typography>
                    {involvedSelected.data.additional && (
                      <Typography variant="body2" component="span">{`${t(
                        "Resolution.employee_number"
                      )}: ${involvedSelected.data.additional}`}</Typography>
                    )}
                    {involvedSelected.data.relationship && (
                      <Typography variant="body2" component="span">{`${t(
                        "Resolution.job_position"
                      )}: ${involvedSelected.data.relationship}`}</Typography>
                    )}
                    {involvedSelected.data.details && (
                      <Typography variant="body2" component="span">{`${t(
                        "Resolution.area"
                      )}: ${involvedSelected.data.details}`}</Typography>
                    )}
                  </Stack>
                }
              />
            </ListItem>
          </Paper>
          <PersonRecords
            records={personRecords}
            actionsKey={actionsKey}
            anotherKey={anotherKey}
            options={isOffenders ? sanctionsOptions : benefitOptions}
          />
          {/* Una fecha por renglon: en columnas los labels se cortan. */}
          <Stack spacing={1.5} mt={1.5}>
            <EGDatePicker
              label={`${t("Resolution.determination_date")}:`}
              value={determinationDate}
              onChange={(date) => setDeterminationDate(date)}
            />
            <EGDatePicker
              label={`${t("Resolution.execution_date")}:`}
              value={executionDate}
              onChange={(date) => setExecutionDate(date)}
            />
          </Stack>

          {isOffenders && (
            <Box mt={1.5}>
              <InvestigationProposals
                investigations={involvedInvestigations}
                type={ACTION_TYPES.SANCTION}
                involvedName={involvedSelected?.data?.name}
                proposals={proposals}
                onProposalsChange={setProposals}
                showErrors={triedToSave}
                allowPending={multipleEnabled}
                // Con registros previos, otro equipo ya esta trabajando a esta
                // persona: nada se aplica sin que alguien lo marque.
                defaultConfirmed={personRecords.length > 0 ? null : true}
                resolvedKeys={getResolvedProposalKeys(personRecords)}
              />

              <Typography
                variant="body2"
                fontWeight={600}
                color="secondary"
                mt={2}
              >
                {t("Resolution.add_on_your_own")}
              </Typography>
            </Box>
          )}

          <Box mt={1.5}>
            {!isCatalogueLoading && (
              <CatalogueMultiSelect
                label={`${titlesType[type].inputLabel}:`}
                placeholder={titlesType[type].inputPlaceholder}
                catalogue={catalogue}
                isOwn={isOwn}
                rootPath={rootPath}
                options={isOffenders ? sanctionsOptions : benefitOptions}
                value={selectedActions}
                onChange={setSelectedActions}
                another={another}
                onAnotherChange={setAnother}
                freeTextLabel={titlesType[type].addActionInput}
                freeTextPlaceholder={
                  titlesType[type].addOtherActionPlaceholder
                }
                addFreeTextTitle={titlesType[type].addAnotherAction}
              />
            )}
            {alreadyRegistered.length > 0 && (
              <Typography fontSize={12} color="warning.main" mt={0.5}>
                {t("Resolution.already_registered_actions", {
                  actions: alreadyRegistered.join(", "),
                })}
              </Typography>
            )}
          </Box>

          {/* Add comment?  */}
          {(!addComment || comment.trim() === "") && (
            <Stack
              display="flex"
              alignItems="center"
              justifyContent="end"
              flexDirection="row"
              mt={0.5}
            >
              <Link onClick={() => setAddComment(true)} underline="hover">
                + {t("Care.add_comment")}
              </Link>
            </Stack>
          )}

          {(addComment || comment.trim() !== "") && (
            <EGInput
              label={`${t("Resolution.details")}: ${comment.length}/500`}
              placeholder={titlesType[type].commentPlaceholder}
              sx={{ mt: 0.5 }}
              multiline
              rows={3}
              value={comment}
              autoFocus
              onChange={(e) => setComment(e.target.value)}
              inputProps={{ maxLength: 500 }}
            />
          )}
        </>
      )}

      <Stack
        display="flex"
        alignItems="center"
        justifyContent="end"
        flexDirection="row"
        flexWrap={{ xs: "wrap", sm: "nowrap" }}
        spacing={2}
        mt={1.5}
        mb={1}
      >
        <EGButton
          label={t("Resolution.save")}
          onClick={onSubmitActions}
          loading={isLoading}
          iconName={isInvalid ? "mark_check" : "mark_check_bold"}
          disabled={isInvalid}
          color={isInvalid ? "light" : "success"}
          iconColor={isInvalid ? "" : "white"}
          iconColorHover={isInvalid ? "" : "white"}
          labelColor={isInvalid ? "" : "white"}
        />
        <EGButton
          iconName="close_icon"
          iconSize={21}
          label={t("Resolution.cancel")}
          onClick={hdlCancel}
          loading={isLoading}
        />
      </Stack>

      <UnsavedChanges
        open={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        onCancel={() => setShowUnsavedModal(false)}
        onConfirm={hdlConfirmLeave}
      />
    </Box>
  );
};
